import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';
import {
  IconButton,
  IIconProps,
  Label,
  MessageBarType,
  PrimaryButton,
  Separator,
  Spinner
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';

// models
import { PhoneViewModel } from '../../../models/Entities/Person/Phone/PhoneModels';

// components
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import PersonPhoneFormComponent from './PersonPhone.Form.Component';

// functions
import * as ComponentFunctions from './PersonPhones.List.Functions';

const EditIcon: IIconProps = { iconName: 'Edit' };
const AddIcon: IIconProps = { iconName: 'Add' };

const PersonPhonesListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedPhoneId, setSelectedPhoneId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (props.PersonId) props.GetPhonesByPersonId(props.PersonId);
  }, [props.PersonId]);

  const handleOpenCreate = () => {
    setSelectedPhoneId(undefined);
    openForm();
  };

  const handleEdit = (phoneId: number) => {
    setSelectedPhoneId(phoneId);
    openForm();
  };

  const handleDismissForm = () => {
    setSelectedPhoneId(undefined);
    dismissForm();
  };

  return (
    <>
      <div className="ComponentHeaderControls">
        <PrimaryButton
          text={t('persons.phones.list.add', { defaultValue: 'Agregar teléfono' })}
          iconProps={AddIcon}
          onClick={handleOpenCreate}
          className="actionButton"
        />
      </div>
      <Separator />

      {props.failOnLoading && (
        <MessageComponent
          message={t('persons.phones.list.error', { defaultValue: 'No fue posible obtener los teléfonos' })}
          subMessage={props.error?.ErrorMessage || ''}
          type={MessageBarType.error}
          itemList={props.error?.Errors || []}
        />
      )}

      {!props.isLoading && props.phones.length === 0 && (
        <MessageComponent
          message={t('persons.phones.list.empty', { defaultValue: 'No hay teléfonos registrados' })}
          type={MessageBarType.info}
        />
      )}

      {props.phones.length > 0 && (
        <table className="Table">
          <thead>
            <tr>
              <th>{t('persons.phones.list.phoneNumber', { defaultValue: 'Número de teléfono' })}</th>
              <th style={{ width: '140px' }}>
                {t('persons.common.actions', { defaultValue: 'Acciones' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {props.phones.map((phone: PhoneViewModel) => (
              <tr key={phone.id} className="TableBodyRow">
                <td className="primary">
                  <Label>{phone.phoneNumber || '-'}</Label>
                </td>
                <td style={{ width: '140px' }}>
                  <IconButton
                    iconProps={EditIcon}
                    title={t('persons.common.edit', { defaultValue: 'Editar' })}
                    ariaLabel={t('persons.common.edit', { defaultValue: 'Editar' })}
                    onClick={() => handleEdit(phone.id)}
                  />
                  <div className="danger">
                    <ConfirmationButtonComponent
                      ButtonId={`PersonPhoneDeleteButton-${phone.id}`}
                      ButtonIconName={'Delete'}
                      ButtonText={''}
                      ButtonCssClass={''}
                      ConfirmationButtonText={t('persons.common.delete', { defaultValue: 'Eliminar' })}
                      ConfirmationButtonCssClass={'deleteButton'}
                      CancelButtonText={t('persons.common.cancel', { defaultValue: 'Cancelar' })}
                      CancelButtonCssClass={''}
                      Title={t('persons.phones.list.deleteTitle', { defaultValue: 'Eliminar teléfono' })}
                      Description={t('persons.phones.list.deleteDescription', {
                        defaultValue: '¿Desea eliminar el teléfono seleccionado?'
                      })}
                      ElementTextHeader={t('persons.phones.list.phoneNumber', { defaultValue: 'Número de teléfono' })}
                      ElementTextInformation={phone.phoneNumber || ''}
                      onConfirmation={() => props.DeletePhone(phone.id)}
                      onCancel={() => undefined}
                      isOperationExecuting={props.isDeleting || false}
                      isOperationExecutedWithError={props.failOnDeleting || false}
                      OperationError={props.error}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {props.isLoading && (
        <div>
          <Spinner
            label={t('persons.phones.list.loading', { defaultValue: 'Cargando teléfonos...' })}
            ariaLive="assertive"
            labelPosition="top"
          />
        </div>
      )}

      <PersonPhoneFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        PersonId={props.PersonId}
        phoneId={selectedPhoneId}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PersonPhonesListComponent as any);
